package com.dynamicruntime.webapp

import com.dynamicruntime.common.home.HMENU
import com.dynamicruntime.common.user.PERSONA
import com.dynamicruntime.common.util.evalTemplate
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.p
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

private val inviteScope = MainScope()

/**
 * What the invitation page says the account is: the client, by its name as the mail names it (issue #1438), and the
 * persona (with the personaSuffix, for a batch user) -- `Hub as Admin`, `Acme as Member B`. Pure, covered under
 * `jsNodeTest`.
 */
fun invitationWhat(info: InvitationInfo): String {
    val persona = PERSONA.label(info.persona) + (if (info.personaSuffix.isEmpty()) "" else " ${info.personaSuffix}")
    return "${info.clientName} as $persona"
}

/**
 * The invitation page (issue #751): reached from a mailed link (`/wa/invite#token=...`). It previews what the
 * link is for and waits for the person to **accept** -- nothing happens on merely opening it, so a mail
 * scanner that follows the link accepts nothing on their behalf. Accepting registers the invited user, verifies
 * a new identity, and signs this browser in as that user; then a **full reload**, since the session has changed
 * hands (the `becomeUser` pattern). Copy from the auth fragment's `invite` namespace.
 */
val InvitePage = FC<Props> {
    val bump = useRefreshBump()
    var copy by useState(Copy.empty)
    var info by useState<InvitationInfo?>(null)
    var error by useState<DisplayError?>(null)
    var busy by useState(false)
    var accepted by useState(false)
    // Read once and held: the address bar loses it on mount, below.
    val token = useState { routeParams()[HMENU.inviteTokenParam]?.trim()?.ifEmpty { null } }.component1()

    fun t(key: String, dflt: String): String = copy.t("invite", key, dflt)

    useEffectOnce {
        // The token leaves the address bar at once (issue #1371), so it is not in the history, a bookmark, or a
        // screenshot; a reload then finds none, and the mailed link opens the page again. It was never sent but in a
        // request body, since a fragment does not reach the server.
        if (token != null) replaceRoute(listOf(HP.page to HMENU.pageInvite))
        inviteScope.launch {
            try {
                val c = AuthApi.fetchConfig()
                copy = fetchCopyWithRetry(c.fragment) { apiResult { AuthApi.fetchConfig().fragment }.valueOrNull() }
                if (token != null) info = AuthApi.previewInvitation(token)
            } catch (e: Throwable) {
                error = userFacingError(e)
            }
        }
    }

    fun accept() {
        val tk = token ?: return
        busy = true
        error = null
        inviteScope.launch {
            try {
                AuthApi.acceptInvitation(tk)
                accepted = true
                // A fresh session as the invited user: home and a re-read, as after a login (issue #1015).
                afterSessionChange(bump)
            } catch (e: Throwable) {
                error = userFacingError(e)
                busy = false
            }
        }
    }

    div {
        className = ClassName("card")
        h1 { +t("title", "You have been invited") }
        error?.let { errorText(it) }
        when {
            token == null -> p {
                className = ClassName("subtitle")
                +t(
                    "noToken",
                    "To see your invitation, open the link in your invitation mail again. This page does not keep the link, " +
                        "so a reload loses it.",
                )
            }
            accepted -> p {
                className = ClassName("subtitle")
                +t("accepted", "You are signed in. The account is now yours.")
            }
            info != null -> {
                val invite = info!!
                p {
                    className = ClassName("subtitle")
                    MarkdownInline {
                        source = t(
                            "summary",
                            $$"An account has been created for **${invite.email}** at **${invite.clientName}**, as **${invite.persona}**.",
                        ).evalTemplate(
                            mapOf(
                                "invite" to mapOf(
                                    "email" to invite.email, "client" to invite.client, "clientName" to invite.clientName,
                                    "persona" to PERSONA.label(invite.persona) + (if (invite.personaSuffix.isEmpty()) "" else " ${invite.personaSuffix}"),
                                ),
                            ),
                        )
                    }
                }
                p {
                    className = ClassName("type-hint")
                    +t("explain", "Accepting proves you can read mail at that address, makes the account yours, and signs you in.")
                }
                div {
                    className = ClassName("row")
                    Button {
                        type = "primary"
                        loading = busy
                        disabled = busy
                        onClick = { accept() }
                        +t("accept", "Accept and sign in")
                    }
                }
            }
        }
    }
}
